import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { ThumbsUp, ThumbsDown, ExternalLink, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import BoringAvatar from "boring-avatars";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatRelativeTime } from "./reports-utils";

interface FeedbackItem {
  id: number;
  created_at: string;
  help_center?: { title: string; slug?: string };
  user_hash?: string;
  feedback_type: string;
  item?: { title: string; slug?: string };
  item_id: number;
  rating: number | null;
  issue_types?: string[];
  feedback_text?: string;
  browser_info?: {
    browser?: string;
    os?: string;
    screen?: string;
    device?: string;
  };
}

interface ReportsTableProps {
  feedback: FeedbackItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onRowClick: (item: FeedbackItem) => void;
  onOpenItem: (e: React.MouseEvent, item: FeedbackItem) => void;
  onPageChange: (page: number) => void;
  formatDate: (dateString: string) => string;
  getRatingIcon: (rating: number | null) => React.ReactNode;
  getItemUrl: (item: FeedbackItem) => string | null;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({
  feedback,
  pagination,
  onRowClick,
  onOpenItem,
  onPageChange,
  formatDate,
  getRatingIcon,
  getItemUrl,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Feedback</CardTitle>
            <CardDescription>
              {pagination.total > 0
                ? `${pagination.total} feedback ${pagination.total === 1 ? "entry" : "entries"} found`
                : "No feedback entries"}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {feedback.length === 0 ? (
          <div className="flex justify-center py-12">
            <EmptyState
              title="No feedback found"
              description="Try adjusting your filters to see more results."
              icons={[MessageSquare]}
            />
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[100px]">Date</TableHead>
                    <TableHead className="min-w-[150px]">Help Center</TableHead>
                    <TableHead className="min-w-[120px]">User</TableHead>
                    <TableHead className="min-w-[100px]">Type</TableHead>
                    <TableHead className="min-w-[150px]">Item</TableHead>
                    <TableHead className="min-w-[80px]">Rating</TableHead>
                    <TableHead className="min-w-[150px]">Issues</TableHead>
                    <TableHead className="min-w-[200px]">Feedback</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.map((item) => {
                    const itemUrl = getItemUrl(item);
                    return (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer"
                        onClick={() => onRowClick(item)}
                      >
                        <TableCell className="max-w-[100px]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-medium cursor-help truncate block">
                                  {formatRelativeTime(item.created_at)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm">{formatDate(item.created_at)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          {item.help_center ? (
                            <div className="font-medium truncate">
                              {item.help_center.title}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[120px]">
                          {item.user_hash ? (
                            <div className="flex items-center gap-2 min-w-0">
                              <BoringAvatar
                                name={item.user_hash}
                                variant="beam"
                                size={32}
                                className="shrink-0"
                              />
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <span className="text-xs text-muted-foreground font-mono cursor-help truncate block">
                                      {item.user_hash.substring(0, 8)}...
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <div>
                                        <strong>User Hash:</strong>
                                      </div>
                                      <div className="font-mono">
                                        {item.user_hash}
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[100px]">
                          <Badge variant="outline" className="truncate max-w-full">
                            {item.feedback_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="font-medium truncate min-w-0 flex-1">
                              {item.item?.title || `ID: ${item.item_id}`}
                            </div>
                            {itemUrl && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 shrink-0"
                                onClick={(e) => onOpenItem(e, item)}
                                title="Open item page"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[80px]">
                          <div className="flex items-center gap-1">
                            {getRatingIcon(item.rating)}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          {item.issue_types && item.issue_types.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.issue_types
                                .slice(0, 2)
                                .map((issue: string, idx: number) => (
                                  <TooltipProvider key={idx}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge
                                          variant="secondary"
                                          className="text-xs max-w-[140px] overflow-hidden"
                                          title={issue}
                                        >
                                          <span className="block truncate">{issue}</span>
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="text-xs max-w-xs">{issue}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ))}
                              {item.issue_types.length > 2 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        +{item.issue_types.length - 2}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="space-y-1">
                                        {item.issue_types
                                          .slice(2)
                                          .map(
                                            (
                                              issue: string,
                                              idx: number
                                            ) => (
                                              <div
                                                key={idx}
                                                className="text-xs"
                                              >
                                                {issue}
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {item.feedback_text ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-sm text-muted-foreground truncate cursor-help">
                                    {item.feedback_text}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                  <p className="text-sm whitespace-pre-wrap">
                                    {item.feedback_text}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                  <span className="font-medium text-foreground">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}
                  </span>{" "}
                  of <span className="font-medium text-foreground">{pagination.total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <div className="text-sm text-muted-foreground px-2">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

